import { create } from "zustand";
import { persist } from "zustand/middleware";

/** Initial theme: saved preference wins, otherwise system preference. */
const getInitialTheme = () => {
  if (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: light)").matches
  ) {
    return "light";
  }
  return "dark";
};

/**
 * Global UI state for the Neovim-inspired portfolio.
 *
 * Phase 0: foundation — buffers, sidebar, mode, opt-in keyboard nav.
 * Phase 1: layout shell components consume this store.
 * Phase 4: interactive features — search, which-key, command mode, floating windows.
 * Phase 5: theme system — persisted via localStorage (theme + keyboardNav).
 */
const useStore = create(
  persist(
    (set) => ({
      // ---- Buffers & navigation ----
      buffers: ["dashboard.md"],
      activeBuffer: "dashboard.md",
      sidebarOpen: true,
      mode: "NORMAL",
      keyboardNav: false,
      dismissedKeyboardHint: false,

      // ---- Theme ----
      theme: getInitialTheme(),
      setTheme: (theme) => set({ theme }),
      toggleTheme: () =>
        set((state) => ({
          theme: state.theme === "dark" ? "light" : "dark",
        })),

      // ---- Buffer actions ----
      setActiveBuffer: (name) => set({ activeBuffer: name }),

      openBuffer: (name) =>
        set((state) => ({
          buffers: state.buffers.includes(name)
            ? state.buffers
            : [...state.buffers, name],
          activeBuffer: name,
        })),

      closeBuffer: (name) =>
        set((state) => {
          const idx = state.buffers.indexOf(name);
          if (idx === -1) return state;
          const buffers = state.buffers.filter((b) => b !== name);
          const activeBuffer =
            state.activeBuffer === name
              ? buffers.length > 0
                ? buffers[Math.max(0, idx - 1)]
                : "dashboard.md"
              : state.activeBuffer;
          return { buffers, activeBuffer };
        }),

      toggleSidebar: () =>
        set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      setMode: (mode) => set({ mode }),

      toggleKeyboardNav: () =>
        set((state) => ({ keyboardNav: !state.keyboardNav })),

      dismissKeyboardHint: () => set({ dismissedKeyboardHint: true }),

      // ---- Telescope search ----
      searchOpen: false,
      searchQuery: "",
      searchResults: [],
      searchIndex: 0,

      openSearch: () =>
        set({
          searchOpen: true,
          searchQuery: "",
          searchResults: [],
          searchIndex: 0,
          whichKeyVisible: false,
          commandModeOpen: false,
          mode: "INSERT",
        }),

      closeSearch: () =>
        set({
          searchOpen: false,
          searchQuery: "",
          searchResults: [],
          searchIndex: 0,
          mode: "NORMAL",
        }),

      setSearchQuery: (query) => set({ searchQuery: query }),
      setSearchResults: (results) => set({ searchResults: results }),
      setSearchIndex: (index) => set({ searchIndex: index }),

      // ---- Which-key menu ----
      whichKeyVisible: false,

      openWhichKey: () =>
        set({
          whichKeyVisible: true,
          searchOpen: false,
          commandModeOpen: false,
        }),
      closeWhichKey: () => set({ whichKeyVisible: false }),

      // ---- Command mode ----
      commandModeOpen: false,
      commandInput: "",
      commandFeedback: null,

      openCommandMode: () =>
        set({
          commandModeOpen: true,
          commandInput: "",
          commandFeedback: null,
          searchOpen: false,
          whichKeyVisible: false,
          mode: "INSERT",
        }),

      closeCommandMode: () =>
        set({
          commandModeOpen: false,
          commandInput: "",
          commandFeedback: null,
          mode: "NORMAL",
        }),

      setCommandInput: (input) => set({ commandInput: input }),
      setCommandFeedback: (feedback) => set({ commandFeedback: feedback }),

      // ---- Floating windows ----
      floatingWindow: null,

      openFloatingWindow: (data) => set({ floatingWindow: data }),
      closeFloatingWindow: () => set({ floatingWindow: null }),

      // ---- Convenience: close all overlays ----
      closeAllOverlays: () =>
        set({
          searchOpen: false,
          searchQuery: "",
          searchResults: [],
          searchIndex: 0,
          whichKeyVisible: false,
          commandModeOpen: false,
          commandInput: "",
          commandFeedback: null,
          floatingWindow: null,
          mode: "NORMAL",
        }),
    }),
    {
      name: "doge-portfolio-store",
      partialize: (state) => ({
        theme: state.theme,
        keyboardNav: state.keyboardNav,
        dismissedKeyboardHint: state.dismissedKeyboardHint,
      }),
    },
  ),
);

export default useStore;
